
import { FSharpRef, Record } from "../../fable_modules/fable-library-js.5.3.0/Types.js";
import { lambda_type, unit_type, option_type, string_type, record_type, bool_type, decimal_type } from "../../fable_modules/fable-library-js.5.3.0/Reflection.js";
import { op_Addition, equals, fromParts, compare, tryParse as tryParse_1 } from "../../fable_modules/fable-library-js.5.3.0/Decimal.js";
import Decimal from "../../fable_modules/fable-library-js.5.3.0/Decimal.js";
import { PledgeCalculation_denominator, PledgeCalculation_perUnitFromTotal, PledgeCalculation_totalFromPerUnit, PledgeCalculation_floorTo } from "../Pledge.js";
import { concat, printf, toText } from "../../fable_modules/fable-library-js.5.3.0/String.js";
import { defaultArg } from "../../fable_modules/fable-library-js.5.3.0/Option.js";
import { MinAllowedGoal_format } from "../Challenge.js";
import { HtmlHelper_createElement } from "../../fable_modules/Feliz.3.3.3/Html.fs.js";
import { margin_mt2, alignItems_itemsEnd, flexDirection_flexRow, gap_gap$002D3, flexDirection_flexCol, border_border, padding_pr3, padding_pl8, padding_py2, boxShadow_shadowXs, borderRadius_roundedMd, width_wFull, padding_pl3, alignItems_itemsCenter, display_flex, position_left0, position_insetY0, position_absolute, margin_mt1, position_relative, fontWeight_fontMedium, fontSize_textSm, display_block, tw } from "../../fable_modules/Feliz.Tailwind.4.0.2/Strings.fs.js";
import { ofArray, singleton } from "../../fable_modules/fable-library-js.5.3.0/List.js";
import { textColor_textNeutral600, sm, borderColor_borderNeutral300, borderColor_borderDanger300, FocusVisible_ringInset, textColor_textNeutral500, textColor_textNeutral700 } from "../TailwindExtensions.js";
import { empty, singleton as singleton_1, append, delay, toList } from "../../fable_modules/fable-library-js.5.3.0/Seq.js";
import { useEffect, useState } from "react";
import React from "react";
import { curry2 } from "../../fable_modules/fable-library-js.5.3.0/Util.js";

/**
 * Result of a committed edit: the 4dp per-unit to store, the 2dp total to display, and whether
 * the typed total had to be snapped down to a representable value.
 */
export class Committed extends Record {
    constructor(PerUnit, DisplayTotal, Snapped) {
        super();
        this.PerUnit = PerUnit;
        this.DisplayTotal = DisplayTotal;
        this.Snapped = Snapped;
    }
}

export function Committed_$reflection() {
    return record_type("Shared.LinkedAmountFields.Committed", [], Committed, () => [["PerUnit", decimal_type], ["DisplayTotal", decimal_type], ["Snapped", bool_type]]);
}

export class Props extends Record {
    constructor(GoalUnits, UnitsCompleted, UnitName, InitialPerUnit, CapAtGoal, Disabled, ErrorMessage, InstanceId, OnChange, AutoFocus, ResetKey) {
        super();
        this.GoalUnits = GoalUnits;
        this.UnitsCompleted = UnitsCompleted;
        this.UnitName = UnitName;
        this.InitialPerUnit = InitialPerUnit;
        this.CapAtGoal = CapAtGoal;
        this.Disabled = Disabled;
        this.ErrorMessage = ErrorMessage;
        this.InstanceId = InstanceId;
        this.OnChange = OnChange;
        this.AutoFocus = AutoFocus;
        this.ResetKey = ResetKey;
    }
}

export function Props_$reflection() {
    return record_type("Shared.LinkedAmountFields.Props", [], Props, () => [["GoalUnits", decimal_type], ["UnitsCompleted", decimal_type], ["UnitName", string_type], ["InitialPerUnit", option_type(decimal_type)], ["CapAtGoal", bool_type], ["Disabled", bool_type], ["ErrorMessage", option_type(string_type)], ["InstanceId", string_type], ["OnChange", lambda_type(option_type(Committed_$reflection()), unit_type)], ["AutoFocus", bool_type], ["ResetKey", string_type]]);
}

/**
 * What the two boxes currently show.
 */
export class BoxState extends Record {
    constructor(PerUnitInput, TotalInput, Snapped, StoredPerUnit) {
        super();
        this.PerUnitInput = PerUnitInput;
        this.TotalInput = TotalInput;
        this.Snapped = Snapped;
        this.StoredPerUnit = StoredPerUnit;
    }
}

export function BoxState_$reflection() {
    return record_type("Shared.LinkedAmountFields.BoxState", [], BoxState, () => [["PerUnitInput", string_type], ["TotalInput", string_type], ["Snapped", bool_type], ["StoredPerUnit", option_type(decimal_type)]]);
}

const cleared = new BoxState("", "", false, undefined);

function invalidEntry(perUnitInput, totalInput) {
    return [new BoxState(perUnitInput, totalInput, false, undefined), undefined];
}

function tryParse(raw) {
    let matchValue;
    let outArg = new Decimal("0");
    matchValue = [tryParse_1(raw, new FSharpRef(() => outArg, (v) => {
        outArg = v;
    })), outArg];
    let matchResult;
    if (matchValue[0]) {
        if (compare(matchValue[1], fromParts(0, 0, 0, false, 0)) >= 0) {
            matchResult = 0;
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return matchValue[1];
        default:
            return undefined;
    }
}

/**
 * Boxes pre-filled from a loaded per-unit amount, or empty.
 */
export function initialState(denominator, initialPerUnit) {
    let arg, arg_1;
    if (initialPerUnit == null) {
        return cleared;
    }
    else {
        const perUnit = initialPerUnit;
        return new BoxState((arg = PledgeCalculation_floorTo(2, perUnit), toText(printf("%.2f"))(arg)), (arg_1 = PledgeCalculation_totalFromPerUnit(perUnit, denominator), toText(printf("%.2f"))(arg_1)), false, perUnit);
    }
}

/**
 * Apply a keystroke in the Total box. Clearing starts over; unparseable text is kept but not committed.
 * The typed total stays as typed; per-unit shows the derived value floored to the cent.
 */
export function editTotal(denominator, raw) {
    let arg;
    if (raw === "") {
        return [cleared, undefined];
    }
    else {
        const matchValue = tryParse(raw);
        if (matchValue != null) {
            const typed = matchValue;
            const perUnit = PledgeCalculation_perUnitFromTotal(typed, denominator);
            const displayTotal = PledgeCalculation_totalFromPerUnit(perUnit, denominator);
            const snapped = !equals(displayTotal, typed);
            return [new BoxState((arg = PledgeCalculation_floorTo(2, perUnit), toText(printf("%.2f"))(arg)), raw, snapped, perUnit), new Committed(perUnit, displayTotal, snapped)];
        }
        else {
            return invalidEntry("", raw);
        }
    }
}

/**
 * Apply a keystroke in the Per-unit box. The typed value is taken at face; total shows the derived value.
 */
export function editPerUnit(denominator, raw) {
    if (raw === "") {
        return [cleared, undefined];
    }
    else {
        const matchValue = tryParse(raw);
        if (matchValue != null) {
            const perUnit = PledgeCalculation_floorTo(4, matchValue);
            const displayTotal = PledgeCalculation_totalFromPerUnit(perUnit, denominator);
            return [new BoxState(raw, toText(printf("%.2f"))(displayTotal), false, perUnit), new Committed(perUnit, displayTotal, false)];
        }
        else {
            return invalidEntry(raw, "");
        }
    }
}

/**
 * On focus-out, replace a snapped total with the amount the stored per-unit actually yields.
 * Live keystrokes keep the typed text so the box does not rewrite itself mid-entry.
 */
export function snapTotal(denominator, current) {
    let arg;
    const matchValue_1 = tryParse(current.TotalInput);
    let matchResult;
    if (current.Snapped) {
        if (matchValue_1 != null) {
            matchResult = 0;
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return new BoxState(current.PerUnitInput, (arg = PledgeCalculation_totalFromPerUnit(PledgeCalculation_perUnitFromTotal(matchValue_1, denominator), denominator), toText(printf("%.2f"))(arg)), current.Snapped, current.StoredPerUnit);
        default:
            return current;
    }
}

/**
 * Recompute the total box from the stored per-unit after the denominator changes (e.g. the cap toggles).
 */
export function rederiveTotal(denominator, current) {
    const matchValue = current.StoredPerUnit;
    if (matchValue == null) {
        return current;
    }
    else {
        let total;
        const arg = PledgeCalculation_totalFromPerUnit(matchValue, denominator);
        total = toText(printf("%.2f"))(arg);
        if (total === current.TotalInput) {
            return current;
        }
        else {
            return new BoxState(current.PerUnitInput, total, false, current.StoredPerUnit);
        }
    }
}

function pastGoal(props) {
    return compare(props.UnitsCompleted, props.GoalUnits) > 0;
}

function denominatorOf(props) {
    return PledgeCalculation_denominator(props.GoalUnits, props.UnitsCompleted, props.CapAtGoal);
}

export function totalLabel(props) {
    if (pastGoal(props) && !props.CapAtGoal) {
        return "Total at finalization (so far)";
    }
    else {
        return "Total at goal";
    }
}

export function helperText(props, state) {
    const unit = props.UnitName;
    if (state.Snapped) {
        const nudge = op_Addition(defaultArg(tryParse(state.PerUnitInput), fromParts(0, 0, 0, false, 0)), fromParts(1, 0, 0, false, 2));
        const nudgeTotal = PledgeCalculation_totalFromPerUnit(nudge, denominatorOf(props));
        const basis = (pastGoal(props) && !props.CapAtGoal) ? "at finalization so far" : "at goal";
        return toText(printf("Per %s rounded down to the nearest cent. Type $%.2f per %s for $%.2f %s."))(unit)(nudge)(unit)(nudgeTotal)(basis);
    }
    else if (pastGoal(props) && !props.CapAtGoal) {
        const arg_5 = MinAllowedGoal_format(props.GoalUnits);
        const arg_7 = MinAllowedGoal_format(props.UnitsCompleted);
        const arg_9 = MinAllowedGoal_format(props.GoalUnits);
        return toText(printf("Linked. Based on the larger of goal (%s %s) or units logged so far (%s %s). Already past the goal of %s %s."))(arg_5)(unit)(arg_7)(unit)(arg_9)(unit);
    }
    else {
        const arg_11 = MinAllowedGoal_format(props.GoalUnits);
        return toText(printf("Linked. Based on goal of %s %s."))(arg_11)(unit);
    }
}

function amountBox(props, id, label, value, autoFocus, onChange, onBlur) {
    return HtmlHelper_createElement("div", ofArray([["className", tw(singleton("flex-1"))], ["children", [HtmlHelper_createElement("label", ofArray([["htmlFor", id], ["className", tw(ofArray([display_block, fontSize_textSm, fontWeight_fontMedium, textColor_textNeutral700]))], ["children", singleton(label)]])), HtmlHelper_createElement("div", ofArray([["className", tw(ofArray([position_relative, margin_mt1]))], ["children", [HtmlHelper_createElement("span", ofArray([["className", tw(ofArray([position_absolute, position_insetY0, position_left0, display_flex, alignItems_itemsCenter, padding_pl3, textColor_textNeutral500]))], ["children", singleton("$")]])), HtmlHelper_createElement("input", toList(delay(() => append(singleton_1(["id", id]), delay(() => append(singleton_1(["type", "number"]), delay(() => append(singleton_1(["inputMode", "decimal"]), delay(() => append(singleton_1(["step", 0.01]), delay(() => append(singleton_1(["min", 0]), delay(() => append(singleton_1(["className", tw(toList(delay(() => append(singleton_1(display_block), delay(() => append(singleton_1(width_wFull), delay(() => append(singleton_1(borderRadius_roundedMd), delay(() => append(singleton_1(boxShadow_shadowXs), delay(() => append(singleton_1(padding_py2), delay(() => append(singleton_1(padding_pl8), delay(() => append(singleton_1(padding_pr3), delay(() => append(singleton_1(FocusVisible_ringInset), delay(() => append(singleton_1(border_border), delay(() => ((props.ErrorMessage != null) ? singleton_1(borderColor_borderDanger300) : singleton_1(borderColor_borderNeutral300)))))))))))))))))))))))]), delay(() => append(singleton_1(["value", value]), delay(() => append(props.Disabled ? singleton_1(["disabled", true]) : empty(), delay(() => append(autoFocus ? singleton_1(["autoFocus", true]) : empty(), delay(() => append(singleton_1(["onChange", (ev) => {
        onChange(ev.target.value);
    }]), delay(() => singleton_1(["onBlur", (_arg) => {
        onBlur();
    }]))))))))))))))))))))))))]]]))]]]));
}

function render(props, state, onTotalChange, onPerUnitChange, onTotalBlur) {
    return HtmlHelper_createElement("fieldset", singleton(["children", [HtmlHelper_createElement("legend", ofArray([["className", tw(singleton("sr-only"))], ["children", singleton("Pledge amount — linked")]])), HtmlHelper_createElement("div", ofArray([["className", tw(ofArray([display_flex, flexDirection_flexCol, gap_gap$002D3, sm(flexDirection_flexRow), sm(alignItems_itemsEnd)]))], ["children", [amountBox(props, concat("pledgeTotal-", props.InstanceId), totalLabel(props), state.TotalInput, props.AutoFocus, onTotalChange, onTotalBlur), HtmlHelper_createElement("span", ofArray([["className", tw(ofArray(["hidden", "sm:block", textColor_textNeutral500, padding_py2]))], ["aria-hidden", "true"], ["children", singleton("↔")]])), amountBox(props, concat("pledgePerUnit-", props.InstanceId), concat("Per ", props.UnitName), state.PerUnitInput, false, onPerUnitChange, () => {
    })]]])), HtmlHelper_createElement("p", toList(delay(() => append(singleton_1(["aria-live", "polite"]), delay(() => append(singleton_1(["className", tw(ofArray([fontSize_textSm, textColor_textNeutral600, margin_mt2]))]), delay(() => (((props.ErrorMessage == null) && !((state.PerUnitInput === "") && (state.TotalInput === ""))) ? singleton_1(["children", singleton(helperText(props, state))]) : empty()))))))))]]));
}

/**
 * Two linked boxes (total and per-unit) that recalculate each other as the user types.
 */
export function view($props) {
    const props = $props.props;
    const denom = denominatorOf(props);
    const patternInput = useState(initialState(denom, props.InitialPerUnit));
    const updateState = patternInput[1];
    const applyEdit = (edit, raw) => {
        const patternInput_1 = edit(denom)(raw);
        updateState((_arg) => patternInput_1[0]);
        props.OnChange(patternInput_1[1]);
    };
    useEffect(() => {
        updateState((_arg_1) => initialState(denom, props.InitialPerUnit));
    }, [props.ResetKey]);
    useEffect(() => {
        updateState((current) => rederiveTotal(denom, current));
    }, [denom]);
    return render(props, patternInput[0], curry2(applyEdit)((denominator) => ((raw_1) => editTotal(denominator, raw_1))), curry2(applyEdit)((denominator_1) => ((raw_2) => editPerUnit(denominator_1, raw_2))), () => {
        updateState((current_1) => snapTotal(denom, current_1));
    });
}

//# sourceMappingURL=LinkedAmountFields.js.map
